import { useFeatureFlagContext, useOverriddenFeatures } from "./styles/components/FeatureFlagsController.mjs";
import { FeatureFlagProvider, FeatureFlagsController, extractFeatureFlagsCookies } from "./styles/components/FeatureFlagsController.mjs";
import { useMemo } from "react";
const toExperimentKeys = (keys) => isSingleExperiment(keys) ? keys.toLowerCase() : keys.map((k) => k.toLowerCase());
const isSingleExperiment = (key) => !Array.isArray(key);
const useIsExperimentsReady = () => {
  const { isExperimentsReady } = useFeatureFlagContext();
  return isExperimentsReady;
};
const useExperiment = (key) => {
  const { experiments } = useFeatureFlagContext();
  const overriddenExperiments = useOverriddenFeatures({
    features: experiments ?? []
  });
  return useMemo(
    () => overriddenExperiments.find(
      (flag) => toExperimentKeys(flag.key) === toExperimentKeys(key)
    ) || void 0,
    [key, overriddenExperiments]
  );
};
const useExperimentVariation = (key) => {
  var _a;
  return (_a = useExperiment(key)) == null ? void 0 : _a.variation;
};
const useExperimentValue = (key) => {
  var _a;
  return (_a = useExperiment(key)) == null ? void 0 : _a.value;
};
const useFeatureFlag = (key) => {
  const { featureFlags } = useFeatureFlagContext();
  const overriddenFeatureFlags = useOverriddenFeatures({
    features: featureFlags ?? []
  });
  const featureFlagData = useMemo(
    () => overriddenFeatureFlags.find(
      (flag) => toExperimentKeys(flag.key) === toExperimentKeys(key)
    ),
    [overriddenFeatureFlags, key]
  );
  return featureFlagData == null ? void 0 : featureFlagData.value;
};
const getExperimentsTrackingNumbersMap = (keys, experimentsConfig) => keys.reduce((allExperiments, k) => {
  const key = toExperimentKeys(k);
  const { trackingNumber } = (experimentsConfig == null ? void 0 : experimentsConfig.find(
    (experiment) => toExperimentKeys(experiment.key) === key
  )) ?? {};
  if (trackingNumber) {
    allExperiments[key] = trackingNumber;
  }
  return allExperiments;
}, {});
const useOverriddenEnrolledExperiments = () => {
  const { enrolledExperiments, experiments, hasOverrides } = useFeatureFlagContext();
  return useOverriddenFeatures({
    features: (hasOverrides ? experiments : enrolledExperiments) ?? []
  });
};
const useHubbleExperiments = () => {
  const enrolledExperiments = useOverriddenEnrolledExperiments();
  return useMemo(() => {
    if (!enrolledExperiments) {
      return {};
    }
    return enrolledExperiments.reduce(
      (allExperiments, { key, variation }) => {
        allExperiments[key] = {
          name: key,
          participantStatus: "enrolled",
          variant: variation
        };
        return allExperiments;
      },
      {}
    );
  }, [enrolledExperiments]);
};
const useHubbleEligibleProps = () => {
  const { applicationName } = useFeatureFlagContext();
  const hubbleExperiments = useHubbleExperiments();
  const enrolledExperiments = useOverriddenEnrolledExperiments();
  const appPrefix = applicationName ? `${applicationName}:` : "";
  return useMemo(() => {
    if (!enrolledExperiments) {
      return {};
    }
    return {
      experiments: hubbleExperiments,
      solMetadataTags: enrolledExperiments.reduce(
        (allTags, { key, variation }) => {
          if (variation) {
            allTags[`seek:${appPrefix}experiments:${key}`] = variation;
          }
          return allTags;
        },
        {}
      )
    };
  }, [enrolledExperiments, hubbleExperiments, appPrefix]);
};
const useTealiumEligibleProps = () => {
  const { experimentsConfig } = useFeatureFlagContext();
  const hubbleExperiments = useHubbleExperiments();
  const enrolledExperiments = useOverriddenEnrolledExperiments();
  const experimentsKeyTrackingNumberMap = useMemo(
    () => getExperimentsTrackingNumbersMap(
      enrolledExperiments.map((flag) => flag.key) ?? [],
      experimentsConfig
    ),
    [enrolledExperiments, experimentsConfig]
  );
  return useMemo(() => {
    if (!enrolledExperiments) {
      return {};
    }
    return {
      experimentsEligible: enrolledExperiments.reduce(
        (all, { key, variation }) => {
          all.push({
            id: key,
            variant: variation,
            num: experimentsKeyTrackingNumberMap[key]
          });
          return all;
        },
        []
      ),
      hubbleExperiments
    };
  }, [enrolledExperiments, experimentsKeyTrackingNumberMap, hubbleExperiments]);
};
const useExperimentsEligibleProps = () => ({
  tealiumEligibleProps: useTealiumEligibleProps(),
  hubbleEligibleProps: useHubbleEligibleProps()
});
export {
  FeatureFlagProvider,
  FeatureFlagsController,
  extractFeatureFlagsCookies,
  useExperiment,
  useExperimentValue,
  useExperimentVariation,
  useExperimentsEligibleProps,
  useFeatureFlag,
  useIsExperimentsReady
};
